<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-support-cours-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="supportForm"
        @submit="addSupportCours"
        @invalid-submit="handleInvalidSubmit"
        :validation-schema="supportSchema"
        :validateOnInput="false"
        :validateOnMount="false"
        :initialValues="{
          nombreHeures: '',
          prerequis: '',
          description: '',
          titre: '',
          auteur: '',
          fichier: null,
          niveau: null,
          filiere: null,
          matiere: null,
          estModulaire: false,
          modulesAdded: []
        }"
      >
        <!-- Ligne 1 : Titre + Niveau + Filière -->
          <div class="row">
            <!-- Titre -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  Titre <span class="text-danger">*</span>
                </label>
                <Field name="titre" type="text"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Titre du support" />
                <ErrorMessage name="titre" class="text-danger" />
              </div>
            </div>

            <!-- Niveau -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  Niveau <span class="text-danger">*</span>
                </label>
                <Field name="niveau" v-slot="{ field }">
                  <Multiselect
                    :options="niveauxOptions"
                    :searchable="true"
                    track-by="value"
                    label="label"
                    v-bind="field"
                    @change="onNiveauChange"
                    placeholder="Sélectionner le niveau"
                    :disabled="isLoadingAssignments"
                  />
                </Field>
                <ErrorMessage name="niveau" class="text-danger" />
              </div>
            </div>

            <!-- Filière -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  Filière <span class="text-danger">*</span>
                </label>
                <Field name="filiere" v-slot="{ field }">
                  <Multiselect
                    :options="filieresOptions"
                    :searchable="true"
                    track-by="value"
                    label="label"
                    v-bind="field"
                    @change="onFiliereChange"
                    placeholder="Sélectionner la filière"
                    :disabled="!selectedNiveau || isLoadingFilieres"
                  />
                </Field>
                <ErrorMessage name="filiere" class="text-danger" />
              </div>
            </div>
          </div>

          <!-- Ligne 2 : Matière + Nombre d'heures + Auteur -->
          <div class="row">
            <!-- Matière -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  Matière <span class="text-danger">*</span>
                </label>
                <Field name="matiere" v-slot="{ field }">
                  <Multiselect
                    :options="matieresOptions"
                    :searchable="true"
                    track-by="value"
                    label="label"
                    v-bind="field"
                    placeholder="Sélectionner la matière"
                    :disabled="!selectedNiveau || !selectedFiliere || isLoadingMatieres"
                  />
                </Field>
                <ErrorMessage name="matiere" class="text-danger" />
              </div>
            </div>

            <!-- Nombre d'heures -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  Nombre d'heures <span class="text-danger">*</span>
                </label>
                <Field name="nombreHeures" type="number" min="1" max="1000"
                  class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 60" />
                <ErrorMessage name="nombreHeures" class="text-danger" />
              </div>
            </div>

            <!-- Auteur -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Auteur</label>
                <Field name="auteur" type="text"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Nom de l'auteur (facultatif)" />
                <ErrorMessage name="auteur" class="text-danger" />
              </div>
            </div>
          </div>

          <!-- Ligne 3 : Fichier + Prérequís + Description -->
          <div class="row">
            <!-- Fichier -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  Fichier principal (PDF) <span class="text-danger">*</span>
                </label>
                <Field name="fichier" type="file" accept="application/pdf"
                  @change="onFichierChange" class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="fichier" class="text-danger" />
                <small v-if="fichierName" class="text-success d-block mt-1">
                  {{ fichierName }}
                </small>
              </div>
            </div>

            <!-- Prérequis -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Prérequis</label>
                <Field name="prerequis" as="textarea" rows="4"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Connaissances requises pour ce cours..." />
                <ErrorMessage name="prerequis" class="text-danger" />
              </div>
            </div>

            <!-- Description -->
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Description</label>
                <Field name="description" as="textarea" rows="4"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Description détaillée du support de cours..." />
                <ErrorMessage name="description" class="text-danger" />
              </div>
            </div>
          </div>

          <!-- Checkbox en bas -->
          <div class="row mt-4">
            <div class="col-md-12">
              <div class="form-check">
                <Field name="estModulaire" type="checkbox" v-slot="{ field }">
                  <input v-bind="field" type="checkbox" id="estModulaire" class="form-check-input"
                    @change="onEstModulaireChange" />
                </Field>
                <label class="form-check-label fw-semibold" for="estModulaire">
                  Ce support est modulaire (divisé en plusieurs modules)
                </label>
              </div>
            </div>
          </div>
        <!-- Section Modules (identique à AddComposition) -->
        <div v-if="estModulaire" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-3 bg-primary">
                <div class="col-md-7 ps-4">
                  <h4 class="fs-4 text-white mb-0">Modules du support</h4>
                </div>
                <div class="col-md-5 pe-4">
                  <button class="btn btn-success float-end" type="button" @click="addNewModule">
                    <i class="fa fa-plus-circle me-1"></i> Ajouter un module
                  </button>
                </div>
              </div>

              <div class="p-4">
                <div v-for="(module, index) in addedModules" :key="module.tempId">
                  <div class="row mt-3 align-items-start">
                    <div class="col-md-3">
                      <label class="d-block text-black fw-semibold mb-2">
                        Code module <span class="text-danger">*</span>
                      </label>
                      <Field :name="`modulesAdded[${index}].code`" v-slot="{ field }">
                        <input v-bind="field" type="text" class="form-control shadow-none"
                          placeholder="Ex: MOD-001" maxlength="50" />
                      </Field>
                      <ErrorMessage :name="`modulesAdded[${index}].code`" class="text-danger" />
                    </div>

                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">
                        Titre du module <span class="text-danger">*</span>
                      </label>
                      <Field :name="`modulesAdded[${index}].titre`" v-slot="{ field }">
                        <input v-bind="field" type="text" class="form-control shadow-none"
                          placeholder="Titre du module" />
                      </Field>
                      <ErrorMessage :name="`modulesAdded[${index}].titre`" class="text-danger" />
                    </div>

                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">
                        Fichier du module (PDF) <span class="text-danger">*</span>
                      </label>
                      <input type="file" accept="application/pdf"
                        @change="(e) => onModuleFichierChange(index, e)"
                        class="form-control shadow-none" />
                      <small v-if="module.fichierName" class="text-success d-block mt-1">
                        {{ module.fichierName }}
                      </small>
                    </div>

                    <div class="col-md-1 d-flex align-items-start pt-4">
                      <button type="button" class="btn btn-danger btn-sm"
                        @click="removeModule(index)" :disabled="addedModules.length === 1">
                        <i class="flaticon-delete"></i>
                      </button>
                    </div>
                  </div>

                  <div class="row mt-3">
                    <div class="col-md-12">
                      <label class="d-block text-black fw-semibold mb-2">Description du module</label>
                      <Field :name="`modulesAdded[${index}].description`" v-slot="{ field }">
                        <textarea v-bind="field" class="form-control shadow-none" rows="3"
                          placeholder="Description optionnelle du module..."></textarea>
                      </Field>
                    </div>
                  </div>

                  <hr class="my-4" v-if="index < addedModules.length - 1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="row mt-5">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Création en cours...' : 'Créer le support de cours' }}
              </button>
              <router-link to="/liste-support" class="btn btn-danger transition border-0 lh-1 fw-medium">
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i> Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error } from "../../utils/utils";
import { useRouter } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';
import { useAuthStore } from "@/services/auth";

interface Option { value: number; label: string; }
interface ModuleTemp { tempId: number; code: string; titre: string; description?: string; fichier?: File | null; fichierName?: string; }

export default defineComponent({
  name: "AddSupportCours",
  components: { Form, Field, ErrorMessage, Multiselect },

  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const supportForm = ref<any>(null);

    const isProfesseur = computed(() => !!authStore.user?.professeur?.id);

    // États du formulaire
    const fichier = ref<File | null>(null);
    const fichierName = ref("");
    const estModulaire = ref(false);
    const addedModules = ref<ModuleTemp[]>([]);
    const isSubmitting = ref(false);
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    let tempModuleId = 1;

    // Options des selects
    const niveauxOptions = ref<Option[]>([]);
    const filieresOptions = ref<Option[]>([]);
    const matieresOptions = ref<Option[]>([]);
    const isLoadingAssignments = ref(true);
    const isLoadingFilieres = ref(false);
    const isLoadingMatieres = ref(false);

    // Assignations professeur
    const professeurAssignments = ref<any>(null);

    // Schéma de validation
    const supportSchema = Yup.object().shape({
       titre: Yup.string().required("Le titre est obligatoire"),
      niveau: Yup.number().required("Le niveau est obligatoire"),
      filiere: Yup.number().required("La filière est obligatoire"),
      matiere: Yup.number().required("La matière est obligatoire"),
      nombreHeures: Yup.number().required("Le nombre d'heures est obligatoire").min(1).max(1000),
      fichier: Yup.mixed().required("Le fichier principal est obligatoire"),
      estModulaire: Yup.boolean(),
      modulesAdded: Yup.array().when("estModulaire", {
        is: true,
        then: (schema) => schema.of(Yup.object().shape({
          code: Yup.string().required("Le code du module est obligatoire"),
          titre: Yup.string().required("Le titre du module est obligatoire"),
          fichier: Yup.mixed().required("Le fichier du module est obligatoire"),
        })).min(1, "Au moins un module requis"),
        otherwise: (schema) => schema.nullable(),
      }),
    });

    const handleInvalidSubmit = () => {
      error("Veuillez corriger les erreurs du formulaire");
    };

    // Gestion fichier principal
    const onFichierChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files?.[0]) {
        fichier.value = input.files[0];
        fichierName.value = input.files[0].name;
        supportForm.value?.setFieldValue("fichier", input.files[0]);
      }
    };

    const onModuleFichierChange = (index: number, e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files?.[0] && addedModules.value[index]) {
        addedModules.value[index].fichier = input.files[0];
        addedModules.value[index].fichierName = input.files[0].name;
        supportForm.value?.setFieldValue(`modulesAdded[${index}].fichier`, input.files[0]);
      }
    };

    const onEstModulaireChange = (e: Event) => {
      const checked = (e.target as HTMLInputElement).checked;
      estModulaire.value = checked;
      if (checked && addedModules.value.length === 0) addNewModule();
      if (!checked) addedModules.value = [];
    };

    const addNewModule = () => {
      addedModules.value.push({
        tempId: tempModuleId++,
        code: "",
        titre: "",
        description: "",
        fichier: null,
        fichierName: "",
      });
    };

    const removeModule = (index: number) => {
      addedModules.value.splice(index, 1);
    };

    // Chargement des assignations (prof ou admin)
    const fetchAssignments = async () => {
      isLoadingAssignments.value = true;
      try {
        if (isProfesseur.value) {
          const res = await ApiService.get('/supports/professeur/assignments');
          professeurAssignments.value = res.data.data;
          niveauxOptions.value = professeurAssignments.value.niveaux.map((n: any) => ({
            value: n.id, label: n.libelle
          }));
        } else {
          // Admin → tous les niveaux
          const res = await ApiService.get('/all/niveaux?limit=0');
          niveauxOptions.value = res.data.data.data.map((n: any) => ({
            value: n.id, label: n.libelle
          }));
        }
      } catch (err) {
        error("Impossible de charger les niveaux");
      } finally {
        isLoadingAssignments.value = false;
      }
    };

    const onNiveauChange = async (val: any) => {
      selectedNiveau.value = val;
      selectedFiliere.value = null;
      filieresOptions.value = [];
      matieresOptions.value = [];
      supportForm.value?.setFieldValue('filiere', null);
      supportForm.value?.setFieldValue('matiere', null);

      if (val && isProfesseur.value && professeurAssignments.value) {
        const filieres = professeurAssignments.value.filieres.filter((f: any) => f.niveauId === val);
        filieresOptions.value = filieres.map((f: any) => ({ value: f.id, label: f.libelle }));
      } else if (val) {
        isLoadingFilieres.value = true;
        const res = await ApiService.get(`/filiereNiveauMatieres?niveau=${val}&limit=0`);
        const uniqueFilieres = Array.from(new Set(res.data.data.data.map((i: any) => i.filiere.id)))
          .map(id => res.data.data.data.find((i: any) => i.filiere.id === id).filiere);
        filieresOptions.value = uniqueFilieres.map((f: any) => ({ value: f.id, label: f.libelle }));
        isLoadingFilieres.value = false;
      }
    };

    const onFiliereChange = async (val: any) => {
      selectedFiliere.value = val;
      matieresOptions.value = [];
      supportForm.value?.setFieldValue('matiere', null);

      if (val && selectedNiveau.value) {
        isLoadingMatieres.value = true;
        if (isProfesseur.value && professeurAssignments.value) {
          const matieres = professeurAssignments.value.matieres.filter(
            (m: any) => m.niveauId === selectedNiveau.value && m.filiereId === val
          );
          matieresOptions.value = matieres.map((m: any) => ({ value: m.id, label: m.libelle }));
        } else {
          const res = await ApiService.get(`/filiereNiveauMatieres?niveau=${selectedNiveau.value}&filiere=${val}&limit=0`);
          matieresOptions.value = res.data.data.data.map((i: any) => ({
            value: i.id,
            label: i.matiere.libelle
          }));
        }
        isLoadingMatieres.value = false;
      }
    };

    const addSupportCours = async (values: any) => {
      isSubmitting.value = true;

      try {
        const formData = new FormData();

        formData.append('nombreHeures', values.nombreHeures);
        formData.append('prerequis', values.prerequis || '');
        formData.append('description', values.description);
        formData.append('titre', values.titre);
        formData.append('auteur', values.auteur || '');
        formData.append('estModulaire', estModulaire.value.toString());
        formData.append('filiereNiveauMatiereId', values.matiere.toString());

        if (fichier.value) formData.append('fichier', fichier.value);

        if (estModulaire.value && values.modulesAdded && values.modulesAdded.length > 0) {
        formData.append('modulesData', JSON.stringify(values.modulesAdded.map((m: any) => ({
          code: m.code?.trim(),
          titre: m.titre?.trim(),
          description: m.description?.trim() || null
        }))));

        values.modulesAdded.forEach((m: any, i: number) => {
          if (addedModules.value[i]?.fichier) {
            formData.append(`moduleFichier_${i}`, addedModules.value[i].fichier!);
          }
        });
      }

        const response = await ApiService.post('/supports', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        success("Support de cours créé avec succès !");
        router.push('/liste-support');

      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la création");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(() => {
      fetchAssignments();
      if (estModulaire.value) addNewModule();
    });

    return {
      supportSchema,
      supportForm,
      niveauxOptions,
      filieresOptions,
      matieresOptions,
      fichierName,
      estModulaire,
      addedModules,
      isSubmitting,
      isLoadingAssignments,
      isLoadingFilieres,
      isLoadingMatieres,
      selectedNiveau,
      selectedFiliere,
      isProfesseur,
      handleInvalidSubmit,
      onFichierChange,
      onModuleFichierChange,
      onEstModulaireChange,
      addNewModule,
      removeModule,
      onNiveauChange,
      onFiliereChange,
      addSupportCours,
    };
  },
});
</script>

<style scoped>
.default-btn {
  background: #28a745;
}
.default-btn:hover {
  background: #218838;
}
</style>